import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { courseAPI, enrollmentAPI } from '../lib/studentAPI';
import type { Section, Chapter, CourseEnrollment } from '../types';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Lock,
  Unlock,
  Play,
  FileText,
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
} from 'lucide-react';

export const LearningPage = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { student } = useAuth();
  const navigate = useNavigate();

  const [sections, setSections] = useState<Section[]>([]);
  const [enrollment, setEnrollment] = useState<CourseEnrollment | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completedChapters, setCompletedChapters] = useState<Set<string>>(new Set());
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  // Modal State (Consistent with Dashboard)
  const [modal, setModal] = useState<{
    show: boolean;
    type: "success" | "error";
    message: string;
  }>({ show: false, type: "success", message: "" });

  const showModal = (type: "success" | "error", message: string) => {
    setModal({ show: true, type, message });
  };

  // Auto hide modal after 3 seconds
  useEffect(() => {
    if (modal.show) {
      const timer = setTimeout(() => {
        setModal((prev) => ({ ...prev, show: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [modal.show]);

  useEffect(() => {
    if (courseId && student) {
      loadLearningData();
    } else if (!student) {
      navigate('/login', { replace: true });
    }
  }, [courseId, student]);

  const loadLearningData = async () => {
    if (!courseId || !student) return;

    try {
      setLoading(true);

      const [sectionsRes, enrollmentsRes] = await Promise.all([
        courseAPI.getCourseSections(courseId),
        enrollmentAPI.getStudentEnrollments(),
      ]);

      // =========================
      // SECTIONS PROCESSING
      // =========================
      const sectionsData: Section[] = (sectionsRes as any)?.data || [];

      const sortedSections = sectionsData
        .sort((a: any, b: any) => a.order - b.order)
        .map((section: any) => ({
          ...section,
          chapters: (section.chapters || []).sort(
            (a: any, b: any) => a.order - b.order
          ),
        }));

      setSections(sortedSections);

      // =========================
      // ENROLLMENTS PROCESSING
      // =========================
      const enrollmentsData = Array.isArray(enrollmentsRes)
        ? enrollmentsRes
        : (enrollmentsRes as any)?.data || [];

      const courseEnrollment = enrollmentsData.find(
        (e: any) =>
          String(e.courseId) === String(courseId) ||
          String(e.course?.id) === String(courseId)
      );

      setEnrollment(courseEnrollment || null);

      // =========================
      // ACCESS LOGIC (FIXED)
      // =========================
      if (!courseEnrollment) {
        showModal("error", "You are not enrolled in this course.");

        const timeout = setTimeout(() => {
          navigate("/dashboard");
        }, 1500);

        return () => clearTimeout(timeout);
      }

      const isPaidOrAccessible =
        courseEnrollment.isUnlocked ||
        courseEnrollment.waitingForStartDate ||
        ["ONGOING", "COMPLETED"].includes(courseEnrollment.status);

      if (!isPaidOrAccessible) {
        showModal("error", "Complete payment to access this course.");

        const timeout = setTimeout(() => {
          navigate("/dashboard");
        }, 1500);

        return () => clearTimeout(timeout);
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load course content. Please try again.";

      showModal("error", errorMsg);
      console.error("Learning page load error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Flatten all chapters (single source of truth for navigation)
  const getAllChapters = () => {
    return sections.flatMap((section, sectionIndex) =>
      (section.chapters || []).map((chapter, chapterIndex) => ({
        ...chapter,
        sectionIndex,
        chapterIndex,
      }))
    );
  };

  // Get current chapter safely
  const getCurrentChapter = (): Chapter | null => {
    const allChapters = getAllChapters();

    const current = allChapters.find(
      (c) =>
        c.sectionIndex === currentSectionIndex &&
        c.chapterIndex === currentChapterIndex
    );

    return current || null;
  };

  // ✅ Proper unlock logic (section-by-section)
  const isWeekUnlocked = (weekIndex: number): boolean => {
    if (weekIndex === 0) return true;

    const prevSection = sections[weekIndex - 1];
    if (!prevSection) return false;

    return prevSection.chapters.every((ch) =>
      completedChapters.has(ch.id)
    );
  };

  // Helper for navigation
  const goToFlatIndex = (index: number) => {
    const allChapters = getAllChapters();
    const target = allChapters[index];
    if (!target) return;

    setCurrentSectionIndex(target.sectionIndex);
    setCurrentChapterIndex(target.chapterIndex);
  };

  // Linear navigation (PREV)
  const handlePrevChapter = () => {
    const allChapters = getAllChapters();

    const currentIndex = allChapters.findIndex(
      (c) =>
        c.sectionIndex === currentSectionIndex &&
        c.chapterIndex === currentChapterIndex
    );

    if (currentIndex > 0) {
      goToFlatIndex(currentIndex - 1);
    }
  };

  // Linear navigation (NEXT)
  const handleNextChapter = () => {
    const allChapters = getAllChapters();

    const currentIndex = allChapters.findIndex(
      (c) =>
        c.sectionIndex === currentSectionIndex &&
        c.chapterIndex === currentChapterIndex
    );

    if (currentIndex < allChapters.length - 1) {
      goToFlatIndex(currentIndex + 1);
    }
  };
  
  const isCourseCompleted = enrollment?.progress === 100;
  const [isCompleting, setIsCompleting] = useState(false);
  const isCurrentChapterCompleted = () => {
    const chapter = getCurrentChapter();
    if (!chapter) return false;
    return completedChapters.has(chapter.id);
  }; 
  
  const handleMarkComplete = async () => {
    if (isCompleting) return;

    const chapter = getCurrentChapter();
    if (!chapter || !enrollment) return;

    // prevent re-completing
    if (completedChapters.has(chapter.id)) return;

    try {
      setIsCompleting(true);

      const result = await enrollmentAPI.markChapterComplete(
        enrollment.id,
        chapter.id
      );

      // ✅ SAFELY extract progress without fighting TS
      const progress =
        (result as any)?.data?.progress ?? (result as any)?.progress;

      // update UI completed state
      setCompletedChapters((prev) => {
        const updated = new Set(prev);
        updated.add(chapter.id);
        return updated;
      });

      // update progress if available
      if (progress !== undefined) {
        setEnrollment((prev: any) =>
          prev ? { ...prev, progress } : prev
        );
      }

      setCompleteDialogOpen(true);

    } catch (err) {
      console.error("Mark complete failed:", err);
    } finally {
      setIsCompleting(false);
    }
  };
  const getYouTubeEmbedUrl = (url: string) => {
    const regExp =
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/;
    const match = url.match(regExp);
    return match
      ? `https://www.youtube.com/embed/${match[1]}`
      : url;
  };
  
  const getSectionProgress = (section: Section) => {
    const total = section.chapters.length;
    const completed = section.chapters.filter(ch =>
      completedChapters.has(ch.id)
    ).length;

    return Math.round((completed / total) * 100);
  };
  const renderChapterContent = (chapter: Chapter) => {
    return (
      <div className="space-y-10">
        <div className="border-b pb-6">
          <h2 className="font-bold mb-2">{chapter.title}</h2>
          {chapter.subtitle && (
            <p className="text-lg text-gray-600">{chapter.subtitle}</p>
          )}

          {chapter.objectives.length > 0 && (
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Learning Objectives</h4>
              <ul className="space-y-2">
                {chapter.objectives.map((obj, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{obj}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Media Content */}
      {chapter.media?.length > 0 && (
        <div className="space-y-6">
          {chapter.media.map((media) => (
            <div key={media.id}>
              {/* VIDEO */}
              {media.type === 'VIDEO' && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    controls
                    className="w-full h-full object-contain"
                    src={media.url}
                  />
                </div>
              )}

              {/* YOUTUBE */}
              {media.type === 'YOUTUBE' && (
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe
                    className="w-full h-full"
                    src={getYouTubeEmbedUrl(media.url)}
                    title={media.name || 'YouTube video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {/* IMAGE */}
              {media.type === 'IMAGE' && (
                <div className="rounded-lg overflow-hidden border">
                  <img
                    src={media.url}
                    alt={media.name || 'Content'}
                    className="w-full max-h-[500px] object-contain"
                  />
                </div>
              )}

              {/* PDF INLINE */}
              {media.type === 'PDF' && (
                <div className="space-y-2">
                  <iframe
                    src={media.url}
                    className="w-full h-[600px] rounded-lg border"
                  />
                  <div className="text-right">
                    <a
                      href={media.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 underline"
                    >
                      Open in new tab
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}


        {chapter.content && (
          <div
            className="prose prose-slate prose-lg max-w-none 
              /* 1. Limit the width of just the text for readability */
              prose-p:max-w-2xl prose-p:mx-auto 
              /* 2. Style the paragraphs */
              prose-p:leading-relaxed prose-p:text-slate-700
              /* 3. Style headings to be bold and centered or distinct */
              prose-headings:max-w-2xl prose-headings:mx-auto prose-headings:font-bold
              /* 4. Style lists to also stay within the readable width */
              prose-ul:max-w-2xl prose-ul:mx-auto prose-ol:max-w-2xl prose-ol:mx-auto"
            dangerouslySetInnerHTML={{ __html: chapter.content }}
          />
        )}
      </div>
    );
  };

      // ✅ Use flattened navigation for buttons
    const allChapters = getAllChapters();

    const currentFlatIndex = allChapters.findIndex(
      (c) =>
        c.sectionIndex === currentSectionIndex &&
        c.chapterIndex === currentChapterIndex
    );

    const hasPrevChapter = currentFlatIndex > 0;
    const hasNextChapter = currentFlatIndex < allChapters.length - 1;

    const currentChapter = getCurrentChapter();
  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading course content...</span>
      </div>
    );
  }

  // Not Enrolled
  if (!enrollment) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="font-bold mb-2">Not Enrolled</h2>
          <p className="text-gray-600 mb-4">You need to enroll in this course first</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-violet-50 via-fuchsia-50 to-rose-50 relative">
      {/* Decorative Flowers Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        {Array.from({ length: 6 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-pink-300"
            initial={{ rotate: 0 }}
            animate={{ rotate: [0, 3, -3, 0] }}
            transition={{ duration: 20 + i, repeat: Infinity, ease: "linear" }}
            style={{
              left: `${10 + i * 15}%`,
              top: `${15 + (i % 3) * 25}%`,
              fontSize: `${2 + i % 3}rem`,
            }}
          >
            🌸
          </motion.div>
        ))}
      </div>
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-lg px-6 py-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="h-8 border-l" />
          <h1 className="font-bold text-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
            Learning Garden
          </h1>
        </div>

        {enrollment && (
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-600">Progress</span>
            <div className="w-48">
              <Progress value={enrollment.progress} className="h-2" />
            </div>
            <span className="font-semibold text-lg">{enrollment.progress}%</span>
          </div>
        )}
      </header>
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Navigation (unchanged) */}
        <div className="w-80 border-r flex-shrink-0 flex flex-col">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold">Course Content</h3>
            <p className="text-sm text-gray-600 mt-1">
              {sections.length} Weeks • {sections.reduce((sum, s) => sum + s.chapters.length, 0)} Chapters
            </p>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-3">
              {sections.map((section, sectionIdx) => {
                const isUnlocked = isWeekUnlocked(sectionIdx);
                const isActive = sectionIdx === currentSectionIndex;

                return (
                  <div key={section.id} className="space-y-2">
                    <button
                      onClick={() => {
                        if (isUnlocked) {
                          setCurrentSectionIndex(sectionIdx);
                          setCurrentChapterIndex(0);
                        }
                      }}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        isActive ? 'bg-blue-50 border-blue-200' : 'bg-white hover:bg-gray-50'
                      } ${!isUnlocked && 'opacity-50 cursor-not-allowed'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold">Week {sectionIdx + 1}</span>
                        {isUnlocked ? (
                          <Unlock className="h-4 w-4 text-green-500" />
                        ) : (
                          <Lock className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{section.title}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {section.chapters.length} chapters
                      </p>
                    </button>

                    {isActive && isUnlocked && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="pl-4 space-y-1"
                      >
                        {section.chapters.map((chapter, chapterIdx) => {
                          const isChapterActive = chapterIdx === currentChapterIndex;
                          const isCompleted = completedChapters.has(chapter.id);

                          return (
                            <button
                              key={chapter.id}
                              onClick={() => setCurrentChapterIndex(chapterIdx)}
                              className={`w-full text-left p-2 rounded text-sm transition-all flex items-center gap-2 ${
                                isChapterActive ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
                              }`}
                            >
                              {isCompleted ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                              ) : (
                                <Play className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              )}
                              <span className="flex-1">{chapter.title}</span>
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Right Panel - Content */}
        <div className="flex-1 flex flex-col bg-white">

          {/* Scrollable Learning Container */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto px-6 py-8 space-y-10">

              {/* 🔝 Top Sticky Header (optional but recommended) */}
              <div className="sticky top-0 z-10 bg-white border-b pb-4 mb-6 flex items-center justify-between">
                <h2 className="font-semibold text-lg truncate">
                  {currentChapter?.title}
                </h2>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePrevChapter}
                    disabled={!hasPrevChapter}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleNextChapter}
                    disabled={!hasNextChapter}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>

              {/* 📘 Chapter Content */}
              <AnimatePresence mode="wait">
                {currentChapter && (
                  <motion.div
                    key={currentChapter.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {renderChapterContent(currentChapter)}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* 🔻 Bottom Sticky Controls */}
              <div className="sticky bottom-0 bg-white border-t pt-4 pb-2 flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevChapter}
                  disabled={!hasPrevChapter}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>

                <Button onClick={handleMarkComplete} disabled={isCompleting}>
                  {isCompleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  {isCompleting ? "Saving..." : "Mark Complete"}
                </Button>

                

                {isCourseCompleted ? (
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => navigate(`/certificate/${courseId}`)}
                  >
                    🎓 Get Certificate
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleNextChapter}
                    disabled={!hasNextChapter}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>

            </div>
          </div>
        </div>
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="max-w-md rounded-2xl border-0 shadow-2xl bg-white">

          <DialogHeader className="text-center">
            <div className="flex justify-center mb-3">
              <div className="bg-[#800000]/10 p-4 rounded-full">
                <CheckCircle2 className="h-10 w-10 text-[#800000]" />
              </div>
            </div>

            <DialogTitle className="text-xl font-semibold text-[#800000]">
              Chapter Complete!
            </DialogTitle>

            <DialogDescription className="text-gray-600 mt-2">
              Great job 🎉 You've successfully finished this chapter.
              
            </DialogDescription>
          </DialogHeader>

          {/* Divider */}
          <div className="h-px bg-gray-200 my-4" />

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 border-gray-300 hover:bg-gray-100"
              onClick={() => setCompleteDialogOpen(false)}
            >
              Stay Here
            </Button>

            <Button
              className="flex-1 bg-[#800000] hover:bg-[#660000] text-white"
              onClick={() => {
                setCompleteDialogOpen(false);
                handleNextChapter();
              }}
            >
              Next Chapter →
            </Button>
          </div>

        </DialogContent>
      </Dialog>

      {/* Global Message Modal */}
      <AnimatePresence>
        {modal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center"
            >
              <div className="flex justify-center mb-6">
                {modal.type === "success" ? (
                  <CheckCircle className="h-20 w-20 text-green-500" />
                ) : (
                  <XCircle className="h-20 w-20 text-red-500" />
                )}
              </div>

              <h3 className="text-2xl font-semibold mb-3">
                {modal.type === "success" ? "Success!" : "Error"}
              </h3>

              <p className="text-gray-600 text-lg leading-relaxed">{modal.message}</p>

              <div className="h-1 bg-gray-100 rounded-full mt-8 overflow-hidden">
                <motion.div
                  className={`h-full ${modal.type === "success" ? "bg-green-500" : "bg-red-500"}`}
                  initial={{ width: "100%" }}
                  animate={{ width: "0%" }}
                  transition={{ duration: 3, ease: "linear" }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
    </div>
  );
}
